import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Einladung, OwnerState, Task, Team } from '../lib/types';
import { TwitchKanäle } from './TwitchKanaele';

/**
 * Der Einrichtungsassistent.
 *
 * Alles, was vor dem Anpfiff zu tun ist, an einem Ort: Einstellungen, Teams, Aufgaben, Mitspieler.
 * Vier Abschnitte in der Reihenfolge, in der man sie braucht – aber frei anspringbar, denn beim
 * zweiten Abend ändert man meist nur noch eine Kleinigkeit.
 *
 * Gespeichert wird sofort, nicht am Ende. Ein Assistent, der alles bis zum Schluss sammelt,
 * verliert bei einem Fehler im letzten Schritt die Arbeit der ersten drei.
 */
export function Einrichtung({
  state,
  onFertig,
}: {
  state: OwnerState;
  onFertig: () => void;
}) {
  const [abschnitt, setAbschnitt] = useState<
    'SPIEL' | 'TEAMS' | 'AUFGABEN' | 'LEUTE' | 'TWITCH'
  >('SPIEL');

  const abschnitte = [
    { id: 'SPIEL', titel: 'Spiel', hinweis: `${state.lobby.boardSize}×${state.lobby.boardSize}` },
    { id: 'TEAMS', titel: 'Teams', hinweis: `${state.teams.length}` },
    {
      id: 'AUFGABEN',
      titel: 'Aufgaben',
      hinweis: `${state.taskCount.active}`,
    },
    { id: 'LEUTE', titel: 'Mitspieler', hinweis: `${state.members.length}` },
    { id: 'TWITCH', titel: 'Twitch', hinweis: state.lobby.challengesEnabled ? 'an' : 'aus' },
  ] as const;

  return (
    <section className="panel setup">
      <div className="setup-head">
        <h2>Einrichtung</h2>
        <button type="button" className="ghost" onClick={onFertig}>
          Fertig
        </button>
      </div>

      <nav className="tabs">
        {abschnitte.map((a) => (
          <button
            key={a.id}
            type="button"
            className={abschnitt === a.id ? 'tab active' : 'tab'}
            onClick={() => setAbschnitt(a.id)}
          >
            {a.titel}
            <span className="tab-zahl">{a.hinweis}</span>
          </button>
        ))}
      </nav>

      {abschnitt === 'SPIEL' && <Spiel state={state} />}
      {abschnitt === 'TEAMS' && <Teams state={state} />}
      {abschnitt === 'AUFGABEN' && <Aufgaben state={state} />}
      {abschnitt === 'LEUTE' && <Leute state={state} />}
      {abschnitt === 'TWITCH' && <TwitchKanäle state={state} />}
    </section>
  );
}

/** Kleiner Helfer: führt eine Aktion aus und zeigt an, was dabei herauskam. */
function useAktion() {
  const [meldung, setMeldung] = useState<{ text: string; gut: boolean } | null>(null);
  const [läuft, setLäuft] = useState(false);

  async function tun(was: () => Promise<unknown>, erfolg: string) {
    setLäuft(true);
    setMeldung(null);
    try {
      await was();
      setMeldung({ text: erfolg, gut: true });
    } catch (err) {
      setMeldung({
        text: err instanceof ApiError ? err.message : 'Das hat nicht geklappt.',
        gut: false,
      });
    } finally {
      setLäuft(false);
    }
  }

  const anzeige = meldung ? (
    <p className={meldung.gut ? 'muted small' : 'notice'}>{meldung.text}</p>
  ) : null;

  return { tun, läuft, anzeige, setMeldung };
}

// --- Spiel -------------------------------------------------------------------

function Spiel({ state }: { state: OwnerState }) {
  const { lobby } = state;
  const { tun, läuft, anzeige } = useAktion();

  const [name, setName] = useState(lobby.name);
  const [größe, setGröße] = useState(lobby.boardSize);
  const [modus, setModus] = useState(lobby.boardMode);
  const [proFeld, setProFeld] = useState(lobby.maxTeamsPerCell);
  const [minuten, setMinuten] = useState(Math.round(lobby.durationSeconds / 60));
  const [challenges, setChallenges] = useState(lobby.challengesEnabled);
  const [pollMode, setPollMode] = useState(lobby.pollMode);

  const läuftRunde = !!lobby.round;

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void tun(
          () =>
            api.updateLobby(lobby.id, {
              name,
              boardSize: größe,
              boardMode: modus,
              maxTeamsPerCell: proFeld,
              durationMinutes: minuten,
              challengesEnabled: challenges,
              pollMode,
            }),
          'Gespeichert.',
        );
      }}
    >
      <label>
        Name des Abends
        <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
      </label>

      <div className="feld-paar">
        <label>
          Boardgröße
          <select value={größe} onChange={(e) => setGröße(Number(e.target.value))}>
            {[3, 4, 5, 6, 7, 8, 9].map((n) => (
              <option key={n} value={n}>
                {n} × {n} ({n * n} Felder)
              </option>
            ))}
          </select>
        </label>

        <label>
          Board
          <select value={modus} onChange={(e) => setModus(e.target.value as typeof modus)}>
            <option value="SHARED">Ein Board für alle</option>
            <option value="INDIVIDUAL">Ein eigenes Board je Team</option>
          </select>
        </label>
      </div>

      <div className="feld-paar">
        <label>
          Teams pro Feld
          <select value={proFeld} onChange={(e) => setProFeld(Number(e.target.value))}>
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n === 1 ? 'Nur eins – wer zuerst klickt' : `Bis zu ${n}`}
              </option>
            ))}
          </select>
        </label>

        <label>
          Spielzeit in Minuten
          <input
            type="number"
            min={1}
            max={1440}
            value={minuten}
            onChange={(e) => setMinuten(Number(e.target.value))}
          />
        </label>
      </div>

      <label className="schalter">
        <input
          type="checkbox"
          checked={challenges}
          onChange={(e) => setChallenges(e.target.checked)}
        />
        <span>Challenges über den Twitch-Chat</span>
      </label>

      {challenges && (
        <label>
          Wer entscheidet
          <select value={pollMode} onChange={(e) => setPollMode(e.target.value as typeof pollMode)}>
            <option value="COMBINED">Alle Kanäle zusammen – eine Challenge für alle</option>
            <option value="PER_CHANNEL">Jeder Kanal für sein Team – eine Challenge je Team</option>
          </select>
        </label>
      )}

      {läuftRunde && (
        <p className="notice calm">
          Es läuft bereits Runde {lobby.round}. Größe und Board-Art wirken sich erst auf die nächste
          Runde aus – die laufende bleibt, wie sie ist.
        </p>
      )}

      <button type="submit" className="primary" disabled={läuft}>
        Speichern
      </button>
      {anzeige}
    </form>
  );
}

// --- Teams -------------------------------------------------------------------

const FARBVORSCHLÄGE = ['#c0392b', '#2980b9', '#27ae60', '#8e44ad'];

function Teams({ state }: { state: OwnerState }) {
  const { tun, läuft, anzeige } = useAktion();
  const [name, setName] = useState('');
  const [farbe, setFarbe] = useState(FARBVORSCHLÄGE[0]!);

  const voll = state.teams.length >= 4;

  return (
    <div className="stack">
      <ul className="zeilen">
        {state.teams.map((team) => (
          <TeamZeile key={team.id} team={team} lobbyId={state.lobby.id} />
        ))}
        {state.teams.length === 0 && <li className="muted small">Noch kein Team angelegt.</li>}
      </ul>

      {voll ? (
        <p className="muted small">Mehr als vier Teams sind nicht vorgesehen – auf einem Feld wird
          es sonst unleserlich.</p>
      ) : (
        <form
          className="zeile-form"
          onSubmit={(e) => {
            e.preventDefault();
            void tun(() => api.addTeam(state.lobby.id, { name, color: farbe }), 'Team angelegt.');
            setName('');
            setFarbe(FARBVORSCHLÄGE[(state.teams.length + 1) % FARBVORSCHLÄGE.length]!);
          }}
        >
          <input
            placeholder="Teamname"
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="color"
            value={farbe}
            onChange={(e) => setFarbe(e.target.value)}
            aria-label="Teamfarbe"
          />
          <button type="submit" disabled={läuft || !name.trim()}>
            Hinzufügen
          </button>
        </form>
      )}
      {anzeige}
    </div>
  );
}

function TeamZeile({ team, lobbyId }: { team: Team; lobbyId: string }) {
  const { tun, läuft, anzeige } = useAktion();
  const [name, setName] = useState(team.name);
  const [farbe, setFarbe] = useState(team.color);

  const geändert = name !== team.name || farbe !== team.color;

  return (
    <li className="zeile">
      <span className="punkt" style={{ background: farbe }} />
      <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
      <input
        type="color"
        value={farbe}
        onChange={(e) => setFarbe(e.target.value)}
        aria-label={`Farbe von ${team.name}`}
      />
      {geändert && (
        <button
          type="button"
          disabled={läuft}
          onClick={() =>
            void tun(() => api.updateTeam(lobbyId, team.id, { name, color: farbe }), 'Gespeichert.')
          }
        >
          Speichern
        </button>
      )}
      <button
        type="button"
        className="ghost gefahr"
        disabled={läuft}
        onClick={() => void tun(() => api.removeTeam(lobbyId, team.id), 'Entfernt.')}
      >
        Entfernen
      </button>
      {anzeige}
    </li>
  );
}

// --- Aufgaben ----------------------------------------------------------------

function Aufgaben({ state }: { state: OwnerState }) {
  const [aufgaben, setAufgaben] = useState<Task[] | null>(null);
  const [filter, setFilter] = useState('');
  const [markiert, setMarkiert] = useState<string[]>([]);
  const [loeschInfo, setLoeschInfo] = useState('');
  const [loescht, setLoescht] = useState(false);
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null);
  const [editTitel, setEditTitel] = useState('');
  const [editBeschreibung, setEditBeschreibung] = useState('');
  const [speichertEdit, setSpeichertEdit] = useState(false);
  const [editFehler, setEditFehler] = useState('');
  const [liste, setListe] = useState('');
  const [zeigeImport, setZeigeImport] = useState(false);
  const { tun, läuft, anzeige } = useAktion();

  const laden = () => {
    void api
      .tasks(state.lobby.id)
      .then(setAufgaben)
      .catch(() => setAufgaben([]));
  };
  useEffect(laden, [state.lobby.id]);

  const nötig = state.lobby.boardSize * state.lobby.boardSize;
  const aktiv = (aufgaben ?? []).filter((a) => a.active).length;
  const sichtbar = (aufgaben ?? []).filter((a) =>
    a.active && a.title.toLowerCase().includes(filter.toLowerCase()),
  );

  function beginneBearbeiten(task: Task) {
    setBearbeiteId(task.id);
    setEditTitel(task.title);
    setEditBeschreibung(task.description ?? '');
    setEditFehler('');
  }

  async function speichereBearbeitung(taskId: string) {
    if (speichertEdit || !editTitel.trim()) return;
    setSpeichertEdit(true);
    setEditFehler('');
    try {
      await api.updateTask(state.lobby.id, taskId, {
        title: editTitel.trim(), description: editBeschreibung.trim(),
      });
      setBearbeiteId(null);
      laden();
    } catch (error) {
      setEditFehler(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichertEdit(false);
    }
  }

  async function markierteLoeschen() {
    if (!markiert.length || loescht) return;
    if (!window.confirm(`${markiert.length} Aufgaben aus der Verwaltung entfernen? Alte Bingo-Boards bleiben erhalten.`)) return;
    setLoescht(true);
    setLoeschInfo('');
    let geloescht = 0;
    const fehlgeschlagen: string[] = [];
    for (const id of markiert) {
      try {
        await api.removeTask(state.lobby.id, id);
        geloescht++;
      } catch {
        fehlgeschlagen.push(id);
      }
    }
    setMarkiert(fehlgeschlagen);
    setLoeschInfo(`${geloescht} Aufgaben aus der Aufgabenliste entfernt.${fehlgeschlagen.length ? ` ${fehlgeschlagen.length} konnten nicht verarbeitet werden.` : ''}`);
    setLoescht(false);
    laden();
  }

  async function markierteDeaktivieren() {
    if (!markiert.length || loescht) return;
    setLoescht(true);
    let erledigt = 0;
    for (const id of markiert) {
      try { await api.updateTask(state.lobby.id, id, { active: false }); erledigt++; } catch { /* Einzelne Fehler werden zusammengefasst. */ }
    }
    setLoeschInfo(`${erledigt} von ${markiert.length} markierten Aufgaben deaktiviert.`);
    setMarkiert([]);
    setLoescht(false);
    laden();
  }

  async function mit(was: () => Promise<unknown>, erfolg: string) {
    await tun(was, erfolg);
    laden();
  }

  return (
    <div className="stack">
      <p className={aktiv < nötig ? 'notice' : 'muted small'}>
        {aktiv} Aufgaben im Pool. Für ein {state.lobby.boardSize}×
        {state.lobby.boardSize}-Board werden {nötig} gebraucht
        {aktiv < nötig ? ` – es fehlen noch ${nötig - aktiv}.` : '.'}
      </p>

      <form
        className="zeile-form"
        onSubmit={(e) => {
          e.preventDefault();
          void tun(async () => {
            await api.addTask(state.lobby.id, { title: titel.trim(), description: beschreibung.trim() });
            setTitel('');
            setBeschreibung('');
            laden();
          }, 'Aufgabe angelegt.');
        }}
      >
        <input
          placeholder="Feldbezeichnung / Titel (max. 500 Zeichen)"
          value={titel}
          maxLength={500}
          onChange={(e) => setTitel(e.target.value)}
        />
        <span className="muted small">{titel.length}/500</span>
        <button type="submit" disabled={läuft || !titel.trim()}>
          Hinzufügen
        </button>
      </form>

      <input
        placeholder="Beschreibung (optional, max. 2000 Zeichen)"
        value={beschreibung}
        maxLength={2000}
        onChange={(e) => setBeschreibung(e.target.value)}
      />

      <div className="row">
        <button type="button" className="ghost" onClick={() => setZeigeImport((z) => !z)}>
          {zeigeImport ? 'Liste schließen' : 'Mehrere auf einmal einfügen'}
        </button>
        <button
          type="button"
          className="ghost"
          disabled={!aufgaben || aktiv === 0}
          onClick={() => exportiere((aufgaben ?? []).filter((a) => a.active), state.lobby.name)}
        >
          Als Liste herunterladen
        </button>
      </div>

      {zeigeImport && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            const zeilen = liste
              .split(/\r?\n/)
              .map((zeile) => zeile.trim().replace(/^\d+[.)]\s*/, ''))
              .filter((zeile) => zeile && !zeile.startsWith('#'))
              .map((zeile) => {
                const trennstelle = zeile.indexOf(' - ');
                return trennstelle < 0
                  ? { title: zeile, description: '' }
                  : {
                      title: zeile.slice(0, trennstelle).trim(),
                      description: zeile.slice(trennstelle + 3).trim(),
                    };
              });
            if (!zeilen.length) return;
            const fehler = zeilen.findIndex((eintrag) => !eintrag.title || eintrag.title.length > 500 || eintrag.description.length > 2000);
            if (fehler !== -1) {
              window.alert(`Aufgabe ${fehler + 1}: Titel maximal 500 Zeichen, Beschreibung maximal 2000 Zeichen. Die Liste bleibt erhalten.`);
              return;
            }
            void tun(async () => {
              await api.importTasks(state.lobby.id, zeilen);
              setListe('');
              setZeigeImport(false);
              laden();
            }, `${zeilen.length} Aufgaben übernommen.`);
          }}
        >
          <textarea
            rows={8}
            placeholder={'Eine Aufgabe je Zeile: Titel - Beschreibung\n3 Pistolen Kills - Töte 3 Gegner mit der Pistole.\n30. Gewinne ein Match - Gewinne eine Runde.'}
            value={liste}
            onChange={(e) => setListe(e.target.value)}
          />
          <p className="muted small">
            Pro Zeile: Titel - Beschreibung. Nummerierungen wie 30. werden entfernt.
            Titel maximal 500, Beschreibung maximal 2000 Zeichen. Bei Fehlern bleibt die Liste erhalten.
          </p>
          <button type="submit" className="primary" disabled={läuft || !liste.trim()}>
            Übernehmen
          </button>
        </form>
      )}

      {aktiv > 8 && (
        <input placeholder="Suchen" value={filter} onChange={(e) => setFilter(e.target.value)} />
      )}

      <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <button type="button" className="ghost" disabled={loescht || sichtbar.length === 0} onClick={() => setMarkiert((alt) => Array.from(new Set([...alt, ...sichtbar.map((t) => t.id)])))}>Sichtbare markieren</button>
        <button type="button" className="ghost" disabled={loescht || markiert.length === 0} onClick={() => setMarkiert([])}>Markierung aufheben</button>
        <button type="button" className="ghost gefahr" disabled={loescht || markiert.length === 0} onClick={() => void markierteLoeschen()}>Markierte löschen ({markiert.length})</button>
        <button type="button" className="ghost" disabled={loescht || markiert.length === 0} onClick={() => void markierteDeaktivieren()}>Markierte deaktivieren</button>
      </div>
      {loeschInfo && <p role="status" className="notice">{loeschInfo}</p>}
      <ul className="zeilen">
        {sichtbar.map((task) => (
          <li key={task.id} className={task.active ? 'zeile' : 'zeile inaktiv'}>
            <input type="checkbox" aria-label={`${task.title} zum Löschen markieren`} checked={markiert.includes(task.id)} onChange={(e) => setMarkiert((alt) => e.target.checked ? [...alt, task.id] : alt.filter((id) => id !== task.id))} />
            <input
              type="checkbox"
              checked={task.active}
              aria-label={`${task.title} verwenden`}
              onChange={(e) =>
                void mit(
                  () => api.updateTask(state.lobby.id, task.id, { active: e.target.checked }),
                  e.target.checked ? 'Wieder dabei.' : 'Kommt nicht mehr aufs Board.',
                )
              }
            />
            {bearbeiteId === task.id ? (
              <div className="stack" style={{ flex: '1 1 320px', minWidth: 0, gap: '0.5rem' }}>
                <label className="small">Titel
                  <input value={editTitel} maxLength={500} onChange={(e) => setEditTitel(e.target.value)} aria-label="Aufgabentitel bearbeiten" />
                </label>
                <label className="small">Beschreibung
                  <textarea value={editBeschreibung} maxLength={2000} rows={3} onChange={(e) => setEditBeschreibung(e.target.value)} aria-label="Aufgabenbeschreibung bearbeiten" />
                </label>
                <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button" className="primary" disabled={speichertEdit || !editTitel.trim()} onClick={() => void speichereBearbeitung(task.id)}>Speichern</button>
                  <button type="button" className="ghost" disabled={speichertEdit} onClick={() => { setBearbeiteId(null); setEditFehler(''); }}>Abbrechen</button>
                </div>
                {editFehler && <p role="alert" className="notice">{editFehler}</p>}
              </div>
            ) : (
              <span className="zeile-text">{task.title}</span>
            )}
            {bearbeiteId !== task.id && <button type="button" className="ghost" disabled={läuft || speichertEdit} onClick={() => beginneBearbeiten(task)}>Bearbeiten</button>}
            <button
              type="button"
              className="ghost gefahr"
              disabled={läuft}
              onClick={() => { if (window.confirm(`Aufgabe „${task.title}“ wirklich löschen?`)) void mit(() => api.removeTask(state.lobby.id, task.id), 'Gelöscht.'); }}
            >
              Löschen
            </button>
          </li>
        ))}
        {aufgaben && sichtbar.length === 0 && (
          <li className="muted small">Keine Aufgaben in dieser Ansicht.</li>
        )}
      </ul>
      {anzeige}
    </div>
  );
}

/**
 * Den Aufgabenpool herunterladen.
 *
 * Eine Aufgabe je Zeile – dasselbe Format, das oben wieder eingefügt werden kann. Dadurch lässt
 * sich ein Pool für den nächsten Abend aufheben oder weitergeben, ohne dass jemand eine
 * Datenbank anfassen muss.
 *
 * Inaktive Aufgaben kommen mit, aber auskommentiert: Sie sollen nicht verschwinden, und beim
 * Wiedereinfügen soll auch nicht stillschweigend alles wieder aktiv sein.
 */
function exportiere(aufgaben: Task[], lobbyName: string): void {
  const zeilen = [
    `# Aufgaben aus „${lobbyName}“`,
    `# ${new Date().toLocaleDateString('de-DE')} · ${aufgaben.length} Aufgaben`,
    '# Zeilen mit # werden beim Einfügen übersprungen.',
    '',
    ...aufgaben.map((a) => {
      const zeile = a.description ? `${a.title} - ${a.description}` : a.title;
      return a.active ? zeile : `# ${zeile}`;
    }),
  ];

  const datei = new Blob([zeilen.join('\n')], { type: 'text/plain;charset=utf-8' });
  const adresse = URL.createObjectURL(datei);

  const verweis = document.createElement('a');
  verweis.href = adresse;
  verweis.download = `aufgaben-${lobbyName.replace(/[^\wäöüÄÖÜß-]+/g, '-').toLowerCase()}.txt`;
  verweis.click();

  // Ohne das bleibt die Datei im Speicher liegen, solange die Seite offen ist.
  URL.revokeObjectURL(adresse);
}

// --- Mitspieler --------------------------------------------------------------

function Leute({ state }: { state: OwnerState }) {
  const { tun, läuft, anzeige } = useAktion();
  const [name, setName] = useState('');
  const [team, setTeam] = useState('');
  const [neuesKonto, setNeuesKonto] = useState<Einladung | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLogin, setEditLogin] = useState('');
  const [editTeams, setEditTeams] = useState<string[]>([]);
  const [visibleTeams, setVisibleTeams] = useState<string[]>([]);
  const [resetPass, setResetPass] = useState<string | null>(null);
  const openEdit = (m: OwnerState['members'][number]) => {
    setEditId(m.userId);
    setEditName(m.displayName);
    setEditLogin(m.username);
    setEditTeams([...m.teamIds]);
    setVisibleTeams([...(m.visibleTeamIds ?? m.teamIds)]);
    setResetPass(null);
  };

  return (
    <div className="stack">
      <ul className="zeilen">
        {state.members.map((m) => (
          <li key={m.userId} className="zeile">
            <span className="zeile-text" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {m.teamIds.map(id => {
                const t = state.teams.find(t => t.id === id);
                return t ? <span key={id} title={t.name} style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, backgroundColor: t.color, flexShrink: 0 }} /> : null;
              })}
              {m.displayName || m.username} {m.isLobbyOwner && <span className="muted small">(Lobbyleitung)</span>}
            </span>
            <span className="muted small">
              {m.teamIds
                .map((id) => state.teams.find((t) => t.id === id)?.name)
                .filter(Boolean)
                .join(', ') || 'ohne Team'}
            </span>
            <span title={m.twitchConnected ? 'Mit Twitch autorisiert' : 'Nicht mit Twitch verbunden'}
              aria-label={m.twitchConnected ? 'Twitch verbunden' : 'Twitch nicht verbunden'}
              style={{ display: 'inline-block', width: 11, height: 11, borderRadius: '50%',
                backgroundColor: m.twitchConnected ? '#22c55e' : '#ef4444', flexShrink: 0 }} />
            <button type="button" className="ghost" onClick={() => openEdit(m)}>Bearbeiten</button>
            <button
              type="button"
              className="ghost gefahr"
              disabled={läuft || m.isLobbyOwner}
              onClick={() =>
                void tun(() => api.removeMember(state.lobby.id, m.userId), 'Ausgetragen.')
              }
            >
              Austragen
            </button>
          </li>
        ))}
        {state.members.length === 0 && (
          <li className="muted small">Noch niemand auf der Liste.</li>
        )}
      </ul>

      {editId && (
        <form className="panel stack" onSubmit={e => {
          e.preventDefault();
          void tun(() => api.editMember(state.lobby.id, editId, {
            displayName: editName, ...(state.members.find(m => m.userId === editId)?.isLobbyOwner ? {} : { username: editLogin }), teamIds: editTeams,
          }).then(async () => {
            for (const teamId of editTeams) {
              await api.teamChatVisibility(state.lobby.id, teamId, editId, visibleTeams.includes(teamId));
            }
          }), 'Spieler und Chat-Anzeige gespeichert.');
        }}>
          <h3>Mitspieler bearbeiten</h3>
          <label>Anzeigename <input value={editName} onChange={e => setEditName(e.target.value)} required maxLength={80} /></label>
          <label>Login-Name <input value={editLogin} onChange={e => setEditLogin(e.target.value)} disabled={state.members.some(m => m.userId === editId && m.isLobbyOwner)} required maxLength={30} /></label>
          <p className="muted small">Teams (mehrere möglich):</p>
          {state.teams.map(t => <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={editTeams.includes(t.id)} onChange={e => setEditTeams(v => e.target.checked ? [...v, t.id] : v.filter(id => id !== t.id))} />
            <span style={{ background: t.color, width: 12, height: 12, display: 'inline-block', borderRadius: 2 }} />{t.name}
          </label>)}
          <p className="muted small">Bei !team im Twitch-Chat anzeigen (nur für zugeordnete Teams):</p>
          {state.teams.filter(t => editTeams.includes(t.id)).map(t => <label key={`sichtbar-${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={visibleTeams.includes(t.id)} onChange={e => setVisibleTeams(v => e.target.checked ? [...v, t.id] : v.filter(id => id !== t.id))} />
            <span style={{ background: t.color, width: 12, height: 12, display: 'inline-block', borderRadius: 2 }} />{t.name}: Spieler im Chat anzeigen
          </label>)}
          <div className="zeile-form">
            <button type="submit" disabled={läuft}>Änderungen speichern</button>
            <button type="button" className="ghost" onClick={() => { setEditId(null); setResetPass(null); }}>Schließen</button>
          </div>
          {!state.members.some(m => m.userId === editId && m.isLobbyOwner) && <button type="button" className="ghost" disabled={läuft} onClick={() => {
            if (!window.confirm('Das Passwort dieses Spielers wirklich zurücksetzen?')) return;
            void tun(async () => {
              const result = await api.resetMemberPassword(state.lobby.id, editId);
              setResetPass(result.password);
            }, 'Neues Passwort erstellt.');
          }}>Passwort zurücksetzen</button>}
          {resetPass && <div className="notice calm zugangsdaten">
            <p>Neues Passwort (jetzt sicher weitergeben):</p>
            <code>{resetPass}</code>
            <button type="button" className="ghost" onClick={() => void navigator.clipboard.writeText(resetPass)}>Kopieren</button>
          </div>}
          <p className="muted small">Das ursprüngliche Erstpasswort kann nicht erneut angezeigt werden, da nur sein Hash gespeichert ist. Stattdessen kannst du ein neues Passwort erstellen. Twitch-Verbindungen werden derzeit separat im Tab Twitch verwaltet.</p>
        </form>
      )}

      <form
        className="zeile-form"
        onSubmit={(e) => {
          e.preventDefault();
          setNeuesKonto(null);
          void tun(async () => {
            const ergebnis = await api.inviteMember(state.lobby.id, {
              username: name,
              ...(team ? { teamIds: [team] } : {}),
            });
            if (ergebnis.created) setNeuesKonto(ergebnis);
          }, 'Eingetragen.');
          setName('');
        }}
      >
        <input
          placeholder="Benutzername"
          value={name}
          maxLength={30}
          onChange={(e) => setName(e.target.value)}
        />
        <select value={team} onChange={(e) => setTeam(e.target.value)}>
          <option value="">ohne Team</option>
          {state.teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="submit" disabled={läuft || !name.trim()}>
          Eintragen
        </button>
      </form>

      <p className="muted small">
        Gibt es das Konto noch nicht, wird es angelegt – mit einem Startpasswort, das du einmal zu
        sehen bekommst.
      </p>

      {neuesKonto?.initialPassword && (
        <div className="notice calm zugangsdaten">
          <p>
            Konto <strong>{neuesKonto.username}</strong> angelegt. Startpasswort:
          </p>
          <code>{neuesKonto.initialPassword}</code>
          <p className="muted small">
            Jetzt weitergeben – es wird nicht noch einmal angezeigt. Gespeichert ist nur der Hash.
          </p>
          <button type="button" className="ghost" onClick={() => setNeuesKonto(null)}>
            Notiert
          </button>
        </div>
      )}
      {anzeige}
    </div>
  );
}
